import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  AlertColor,
  AlertDescriptionProps,
  AlertDirection,
  AlertIconProps,
  AlertProps,
  AlertTitleProps,
  AlertVariant,
} from './types';

const colorClasses: Record<AlertColor, string> = {
  default: '',
  info: 'alert-info',
  success: 'alert-success',
  warning: 'alert-warning',
  error: 'alert-error',
};

const variantClasses: Record<AlertVariant, string> = {
  default: '',
  outline: 'alert-outline',
  dash: 'alert-dash',
  soft: 'alert-soft',
};

const directionClasses: Record<AlertDirection, string> = {
  vertical: 'alert-vertical',
  horizontal: 'alert-horizontal',
};

const AlertBase = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      color = 'default',
      variant = 'default',
      direction,
      role = 'alert',
      children,
      ...props
    },
    ref,
  ) => {
    const classes = twMerge(
      'alert',
      colorClasses[color],
      variantClasses[variant],
      direction ? directionClasses[direction] : '',
      className,
    );

    return (
      <div ref={ref} role={role} className={classes} {...props}>
        {children}
      </div>
    );
  },
);

const Icon = forwardRef<HTMLSpanElement, AlertIconProps>(
  ({ className, children, ...props }, ref) => (
    <span
      ref={ref}
      className={twMerge('shrink-0 [&>svg]:size-6', className)}
      {...props}
    >
      {children}
    </span>
  ),
);

const Title = forwardRef<HTMLHeadingElement, AlertTitleProps>(
  ({ className, children, ...props }, ref) => (
    <h3
      ref={ref}
      className={twMerge('text-sm font-bold leading-6', className)}
      {...props}
    >
      {children}
    </h3>
  ),
);

const Description = forwardRef<HTMLParagraphElement, AlertDescriptionProps>(
  ({ className, children, ...props }, ref) => (
    <p ref={ref} className={twMerge('text-xs', className)} {...props}>
      {children}
    </p>
  ),
);

AlertBase.displayName = 'Alert';
Icon.displayName = 'Alert.Icon';
Title.displayName = 'Alert.Title';
Description.displayName = 'Alert.Description';

export const Alert = Object.assign(AlertBase, {
  Icon,
  Title,
  Description,
});
